import { createContext, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { getToken, setToken, clearToken, isTokenExpired, getTokenPayload } from "@/lib/auth";
import { api } from "@/lib/api";

export interface AuthState {
  token: string | null;
  email: string | null;
  isAuthenticated: boolean;
  login: (token: string) => void;
  logout: () => void;
}

export const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Schedule a token refresh 5 minutes before expiry
  const scheduleRefresh = useCallback((token: string) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

    const payload = getTokenPayload();
    if (!payload?.exp) return;

    const msUntilExpiry = (payload.exp * 1000) - Date.now();
    const refreshIn = Math.max(msUntilExpiry - 5 * 60 * 1000, 0); // 5 min before expiry

    refreshTimerRef.current = setTimeout(async () => {
      try {
        const result = await api.refreshToken();
        setToken(result.token);
        setTokenState(result.token);
        setEmail(result.email);
        scheduleRefresh(result.token);
      } catch {
        // Refresh failed — user will need to re-authenticate
        clearToken();
        setTokenState(null);
        setEmail(null);
      }
    }, refreshIn);
  }, []);

  // On mount, hydrate from localStorage
  useEffect(() => {
    const stored = getToken();
    if (stored && !isTokenExpired(stored)) {
      setTokenState(stored);
      const payload = getTokenPayload();
      setEmail(payload?.email ?? null);
      scheduleRefresh(stored);
    } else if (stored) {
      // Token exists but expired — try to refresh
      api.refreshToken()
        .then((result) => {
          setToken(result.token);
          setTokenState(result.token);
          setEmail(result.email);
          scheduleRefresh(result.token);
        })
        .catch(() => {
          clearToken();
        });
    }

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [scheduleRefresh]);

  const login = useCallback((newToken: string) => {
    setToken(newToken);
    setTokenState(newToken);
    try {
      const parts = newToken.split(".");
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))) as { email?: string };
        setEmail(payload.email ?? null);
      }
    } catch {
      setEmail(null);
    }
    scheduleRefresh(newToken);
  }, [scheduleRefresh]);

  const logout = useCallback(() => {
    clearToken();
    setTokenState(null);
    setEmail(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      token,
      email,
      isAuthenticated: token !== null,
      login,
      logout,
    }),
    [token, email, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
